import { SparkleIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import CharacterCount from "../../../basic/CharacterCount";
import InputWithActions from "../../../basic/inputWithActions/InputWithActions";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import { ClassActivityCategory } from "../../../constants/ClassActivityConstants";
import { MAX_NAME_LENGTH } from "../../../constants/LimitConstants";
import { useRTContext } from "../../../context/RTContext";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { VocabTrainingTitleTags } from "../../../enums/vocabTrainingTitleTags";
import { formatTitleDate, getLimitedBookTitle, getSelectedUnitsTitle } from "../../../helpers/VocabTrainingTitle";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import { ClassAssignmentPopup } from "../../classActivityCard/classTag/ClassAssignmentPopup";
import { ChipTag } from "./ChipTag";

const PREDEFINED_TAGS = [
    VocabTrainingTitleTags.BASED_ON_VOCAB,
    VocabTrainingTitleTags.TODAYS_DATE,
    VocabTrainingTitleTags.BASED_ON_BOOK,
];

const FLASH_DURATION_MS = 800;

interface VocabTrainingTitleBarProps {
    hideBookTag?: boolean;
}

const VocabTrainingTitleBar = (props: VocabTrainingTitleBarProps) => {
    const {
        testName,
        setTestName,
        selectedTitleTag,
        setSelectedTitleTag,
        selectedBookData,
        selectedBandUnits,
        vocabularyDrawerContent,
        selectedUserSubject,
    } = useTestCreationContext();
    const {
        schoolClasses,
        currentExerciseSchoolClassId,
        setCurrentExerciseSchoolClassId,
        reloadSchoolClasses,
        hasLoadedSchoolClasses,
    } = useRTContext();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState(testName);
    const [isClassPopupOpen, setIsClassPopupOpen] = useState(false);
    const [triggerFlash, setTriggerFlash] = useState(false);
    const [tagBeforeEdit, setTagBeforeEdit] = useState<VocabTrainingTitleTags | null>(null);
    const [resolvedTitleBeforeEdit, setResolvedTitleBeforeEdit] = useState("");

    const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const t = useT();
    const t_titleLabel = t("Title of the vocabulary training:", { _tags: "CreateVocabTraining,VocabTrainingTitleBar" });
    const t_customTitlePlaceholder = t("Custom title (optional)", {
        _tags: "CreateVocabTraining,VocabTrainingTitleBar",
    });
    const t_classLabel = t("Class:", { _tags: "CreateVocabTraining,VocabTrainingTitleBar" });
    const t_chooseClassPlaceholder = t("Choose class (optional)", {
        _tags: "CreateVocabTraining,VocabTrainingTitleBar",
    });
    const t_basedOnVocab = t("Based on vocabulary", { _tags: "CreateVocabTraining,VocabTrainingTitleBar" });
    const t_todaysDate = t("Today's date", { _tags: "CreateVocabTraining,VocabTrainingTitleBar" });
    const t_basedOnBook = t("Based on the book", { _tags: "CreateVocabTraining,VocabTrainingTitleBar" });
    const t_vocabularyFromDate = t("Vocabulary from {date}", {
        _tags: "CreateVocabTraining,VocabTrainingTitleBar",
        date: formatTitleDate(),
    });
    const t_removeTitle = t("Remove title", { _tags: "CreateVocabTraining,VocabTrainingTitleBar" });

    // Keep the editable draft in sync with the stored title whenever we are not actively editing
    // (e.g. after a tag is selected/removed elsewhere).
    useEffect(() => {
        if (!isEditingTitle) {
            setTitleDraft(testName);
        }
    }, [testName, isEditingTitle]);

    // Load the teacher's classes into the /create RTContext instance if they haven't been fetched
    // yet, so the class picker has options. This must live here (not at the flow entry points): the
    // /create flow uses its own RTContextProvider, separate from the assistant's instance. Guard on
    // the load flag (not the list length) so teachers with no classes don't re-fetch on every mount.
    useEffect(() => {
        if (!hasLoadedSchoolClasses) {
            reloadSchoolClasses();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            if (flashTimerRef.current !== null) {
                clearTimeout(flashTimerRef.current);
            }
        };
    }, []);

    const getTagLabel = (tag: VocabTrainingTitleTags): string => {
        switch (tag) {
            case VocabTrainingTitleTags.BASED_ON_VOCAB:
                return t_basedOnVocab;
            case VocabTrainingTitleTags.TODAYS_DATE:
                return t_todaysDate;
            case VocabTrainingTitleTags.BASED_ON_BOOK:
                return t_basedOnBook;
            default:
                return "";
        }
    };

    const getTitleForTag = (tag: VocabTrainingTitleTags): string => {
        switch (tag) {
            case VocabTrainingTitleTags.BASED_ON_VOCAB:
                return getSelectedUnitsTitle({ selectedBandUnits, vocabularyDrawerContent, selectedUserSubject, t });
            case VocabTrainingTitleTags.TODAYS_DATE:
                return t_vocabularyFromDate;
            case VocabTrainingTitleTags.BASED_ON_BOOK:
                // Avoid the empty "() ()" template before a book is actually chosen.
                return selectedBookData.publisherBook?.BookName ? getLimitedBookTitle(selectedBookData) : "";
            default:
                return "";
        }
    };

    // The selected tag's title is resolved live, so it fills in once the underlying data is
    // available (e.g. the book or vocabulary is picked on a following screen).
    const resolvedTagTitle = selectedTitleTag !== null ? getTitleForTag(selectedTitleTag) : "";

    // Mirror the resolved tag title into the stored name — including when it resolves back to empty
    // (e.g. all vocabulary gets unselected again), so the persisted name never diverges from what
    // the chip shows. The save-time fallback supplies a default for empty names.
    useEffect(() => {
        if (selectedTitleTag !== null) {
            setTestName(resolvedTagTitle);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTitleTag, resolvedTagTitle]);

    const clearEditSnapshot = () => {
        setTagBeforeEdit(null);
        setResolvedTitleBeforeEdit("");
        setIsEditingTitle(false);
    };

    const handleSelectTag = (tag: VocabTrainingTitleTags) => {
        setSelectedTitleTag(tag);
        // Clear any previously typed custom title; the resolved tag value fills in via the effect above.
        setTestName("");
        clearEditSnapshot();
        if (flashTimerRef.current !== null) {
            clearTimeout(flashTimerRef.current);
        }
        setTriggerFlash(true);
        flashTimerRef.current = setTimeout(() => setTriggerFlash(false), FLASH_DURATION_MS);
    };

    const handleRemoveTag = () => {
        setSelectedTitleTag(null);
        setTestName("");
        setTitleDraft("");
        clearEditSnapshot();
    };

    const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setTitleDraft(event.target.value);
    };

    const handleSaveTitle = () => {
        const titleChanged = titleDraft !== resolvedTitleBeforeEdit;
        if (tagBeforeEdit !== null && !titleChanged) {
            setSelectedTitleTag(tagBeforeEdit);
        } else {
            setTestName(titleDraft);
        }
        clearEditSnapshot();
    };

    const handleDiscardTitle = () => {
        if (tagBeforeEdit !== null) {
            setSelectedTitleTag(tagBeforeEdit);
        } else {
            setTitleDraft(testName);
        }
        clearEditSnapshot();
    };

    const handleEditResolvedTitle = () => {
        setTagBeforeEdit(selectedTitleTag);
        setResolvedTitleBeforeEdit(resolvedTagTitle);
        setTitleDraft(resolvedTagTitle);
        setSelectedTitleTag(null);
        setIsEditingTitle(true);
    };

    const handleSelectClass = (schoolClassId: string | null) => {
        setCurrentExerciseSchoolClassId(schoolClassId ?? "");
        return true;
    };

    const selectedClass = schoolClasses.find((schoolClass) => schoolClass.token === currentExerciseSchoolClassId);

    const renderTitleValue = () => {
        const flashClass = triggerFlash ? "animate-chip-flash" : "";

        if (selectedTitleTag !== null && resolvedTagTitle) {
            return (
                <button
                    type="button"
                    data-testid="vocab-training-title-resolved"
                    onClick={handleEditResolvedTitle}
                    className="flex-grow w-full min-w-[0px] min-h-[2.5rem] flex items-center gap-xs pl-s cursor-pointer"
                >
                    <SparkleIcon
                        size={20}
                        className="text-p6-grey-dark shrink-0"
                    />
                    <span className="flex-grow min-w-[0px] truncate text-l text-p6-grey-dark text-left">
                        {resolvedTagTitle}
                    </span>
                    <PhaseSixIcon
                        name={"edit-avatar"}
                        className="text-p6-grey-dark text-xxl leading-none shrink-0"
                    />
                </button>
            );
        }

        if (selectedTitleTag !== null) {
            return (
                <div className={`flex-grow min-h-[2.5rem] flex items-center px-s rounded-button ${flashClass}`}>
                    <div className="flex items-center gap-xs py-xxs">
                        <SparkleIcon
                            size={20}
                            className="text-p6-grey-mid shrink-0"
                        />
                        <span className="text-l text-p6-grey-mid">{getTagLabel(selectedTitleTag)}</span>
                        <button
                            type="button"
                            aria-label={t_removeTitle}
                            onClick={handleRemoveTag}
                            className="flex items-center cursor-pointer"
                        >
                            <PhaseSixIcon
                                name={"close"}
                                className="text-p6-grey-mid text-xl leading-none"
                            />
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="flex-grow w-full min-w-[0px] flex flex-col gap-xxs">
                <div className="flex items-center min-h-[2.5rem]">
                    <InputWithActions
                        data-testid="vocab-training-title-input"
                        isEditing={isEditingTitle}
                        setIsEditing={setIsEditingTitle}
                        currentText={titleDraft}
                        placeholder={t_customTitlePlaceholder}
                        maxLength={MAX_NAME_LENGTH}
                        onChange={handleTitleChange}
                        onSave={handleSaveTitle}
                        onDiscard={handleDiscardTitle}
                    />
                </div>
                {isEditingTitle && (
                    <CharacterCount
                        currentLength={titleDraft.length}
                        maxLength={MAX_NAME_LENGTH}
                        dataTestId="vocab-training-title-character-count"
                    />
                )}
            </div>
        );
    };

    return (
        <div
            data-testid="vocab-training-title-bar"
            className="bg-white px-s py-s flex flex-col gap-s"
        >
            <div
                className={`flex ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "flex-row items-start",
                    "flex-col items-start"
                )} gap-s`}
            >
                <span
                    className={`flex items-center min-h-[2.5rem] text-l font-semibold text-p6-grey-dark ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "whitespace-nowrap"
                    )}`}
                >
                    {t_titleLabel}
                </span>
                {renderTitleValue()}
            </div>

            <div className="flex flex-wrap gap-s">
                {PREDEFINED_TAGS.filter(
                    (tag) =>
                        tag !== selectedTitleTag &&
                        tag !== tagBeforeEdit &&
                        !(props.hideBookTag && tag === VocabTrainingTitleTags.BASED_ON_BOOK)
                ).map((tag) => (
                    <ChipTag
                        key={tag}
                        data-testid={`vocab-training-title-tag-${tag}`}
                        label={getTagLabel(tag)}
                        onClick={() => handleSelectTag(tag)}
                    />
                ))}
            </div>

            <div className="flex items-center gap-xs min-h-[2.5rem]">
                <span
                    className={`text-l font-semibold text-p6-grey-dark ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "whitespace-nowrap"
                    )}`}
                >
                    {t_classLabel}
                </span>
                <button
                    type="button"
                    data-testid="vocab-training-class-edit"
                    aria-label={t_chooseClassPlaceholder}
                    onClick={() => setIsClassPopupOpen(true)}
                    className="flex-grow w-full min-w-[0px] flex items-center gap-xs cursor-pointer"
                >
                    {selectedClass ? (
                        <span className="min-w-[0px] truncate text-l text-p6-grey-dark text-left">
                            {selectedClass.name}
                        </span>
                    ) : (
                        <span className="min-w-[0px] truncate text-l text-p6-grey-mid text-left">
                            {t_chooseClassPlaceholder}
                        </span>
                    )}
                    <PhaseSixIcon
                        name={"edit-avatar"}
                        className="text-p6-grey-dark text-xl leading-none shrink-0"
                    />
                </button>
            </div>

            <ClassAssignmentPopup
                isOpen={isClassPopupOpen}
                onClose={() => setIsClassPopupOpen(false)}
                availableSchoolClasses={schoolClasses}
                selectedSchoolClassId={currentExerciseSchoolClassId || undefined}
                classActivityCategory={ClassActivityCategory.VOCAB_TRAINING}
                onSchoolClassChange={handleSelectClass}
            />
        </div>
    );
};

export default VocabTrainingTitleBar;
